/**
 * RemoteLogger — Lightweight remote diagnostics for playback issues.
 *
 * Captures key events from the playback chain and batches them to the
 * server every 5 seconds. Enabled by setting localStorage key
 * `maestro_debug_logging` to '1'. Auto-starts when enabled.
 *
 * TEMPORARY — remove after playback issues are resolved.
 */

import { CLOUD_API_URL } from '../config/serverConfig';

interface LogEntry {
  ts: number;
  src: string;
  evt: string;
  data?: Record<string, any>;
}

const MAX_BUFFER = 500;
const FLUSH_INTERVAL_MS = 5000;

let _buffer: LogEntry[] = [];
let _sessionId = '';
let _device = 'unknown';
let _flushTimer: ReturnType<typeof setInterval> | null = null;
let _started = false;
let _timerCount = 0;
let _listenerCount = 0;

function getSessionId(): string {
  if (_sessionId) return _sessionId;
  try {
    const existing = sessionStorage.getItem('maestro_debug_session');
    if (existing) { _sessionId = existing; return existing; }
  } catch {}
  _sessionId = `dbg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  try { sessionStorage.setItem('maestro_debug_session', _sessionId); } catch {}
  return _sessionId;
}

function detectDevice(): string {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('ipad')) return 'iPad';
  if (ua.includes('iphone')) return 'iPhone';
  if ((window as any).__MAESTRO_CONNECTOR__) return 'Desktop';
  return 'Browser';
}

export function isDebugLoggingEnabled(): boolean {
  try { return localStorage.getItem('maestro_debug_logging') === '1'; } catch { return false; }
}

export function rlog(source: string, event: string, data?: Record<string, any>): void {
  if (!_started) return;
  const entry: LogEntry = { ts: Date.now(), src: source, evt: event };
  if (data) entry.data = data;
  _buffer.push(entry);
  if (_buffer.length > MAX_BUFFER) _buffer = _buffer.slice(-MAX_BUFFER);
}

export function rlogTimerCreated(source: string, name: string): void {
  _timerCount++;
  rlog(source, 'timer_created', { name, totalTimers: _timerCount });
}

export function rlogTimerCleared(source: string, name: string): void {
  _timerCount = Math.max(0, _timerCount - 1);
  rlog(source, 'timer_cleared', { name, totalTimers: _timerCount });
}

export function rlogListenerAdded(source: string, name: string): void {
  _listenerCount++;
  rlog(source, 'listener_added', { name, totalListeners: _listenerCount });
}

export function rlogListenerRemoved(source: string, name: string): void {
  _listenerCount = Math.max(0, _listenerCount - 1);
  rlog(source, 'listener_removed', { name, totalListeners: _listenerCount });
}

async function flush(): Promise<void> {
  if (_buffer.length === 0) return;
  const entries = _buffer.splice(0, _buffer.length);
  try {
    await fetch(`${CLOUD_API_URL}/api/debug/log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId: getSessionId(),
        device: _device,
        entries,
      }),
    });
  } catch {
    // Re-add entries on failure (capped)
    _buffer = [...entries, ..._buffer].slice(-MAX_BUFFER);
  }
}

export function startRemoteLogging(): void {
  if (_started) return;
  _started = true;
  _device = detectDevice();
  _timerCount = 0;
  _listenerCount = 0;

  rlog('logger', 'session_start', {
    device: _device,
    sessionId: getSessionId(),
    userAgent: navigator.userAgent,
    url: location.href,
  });

  _flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);

  // Flush on page hide (iOS Safari background)
  window.addEventListener('pagehide', flush);
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
    rlog('logger', 'visibility', { state: document.visibilityState });
  });

  console.log('[RemoteLogger] Started — session:', getSessionId(), 'device:', _device);
}

export function stopRemoteLogging(): void {
  if (!_started) return;
  _started = false;
  if (_flushTimer) { clearInterval(_flushTimer); _flushTimer = null; }
  flush();
  console.log('[RemoteLogger] Stopped');
}

// Auto-start if enabled
if (isDebugLoggingEnabled()) {
  startRemoteLogging();
}
